import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Chip, Field } from '@promtver/ui-kit';

import { useAuth } from '../auth/AuthProvider';
import { homeRouteFor } from '../auth/session';
import { ApiError, errorMessage, isNetworkError } from '../api/errors';
import { RegistrationForm } from '../features/registration/RegistrationForm';
import { ru } from '../i18n/ru';

const MIN_PASSWORD_LENGTH = 12;

/** Режим страницы: вход по учётной записи или регистрация нового предприятия. */
type Mode = 'login' | 'registration';

/**
 * Вход в панель предприятия: e-mail/пароль (ТЗ §4.3).
 * Пароль администраторов — не менее 12 символов; при требовании сервера
 * на 2FA запрашивается код подтверждения.
 *
 * Здесь же — форма регистрации предприятия: представитель без учётной записи
 * не может войти, поэтому регистрация должна быть на расстоянии одного клика
 * от формы входа (FR-B2B-01).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { user, login, confirmTwoFactor } = useAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(homeRouteFor(user), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(ru.login.passwordHint);
      return;
    }

    setBusy(true);
    try {
      const result = await login(email, password);
      if (result.kind === 'two_factor') {
        setChallengeId(result.challengeId);
        return;
      }
      navigate(homeRouteFor(result.session.user), { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError) {
        if (cause.code === 'invalid_credentials') {
          setError(ru.login.errorInvalid);
        } else if (cause.code === 'invalid_two_factor_code') {
          setError(ru.login.errorTwoFactor);
        } else if (cause.code === 'account_blocked') {
          setError(ru.login.errorBlocked);
        } else if (cause.code === 'two_factor_required') {
          setError(ru.login.errorTwoFactor);
        } else {
          setError(errorMessage(cause, ru.common.unknownError));
        }
      } else if (isNetworkError(cause)) {
        setError(ru.login.errorNetwork);
      } else {
        setError(errorMessage(cause, ru.common.unknownError));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!challengeId) return;
    setError(null);
    setBusy(true);
    try {
      const confirmedUser = await confirmTwoFactor(challengeId, code.trim());
      navigate(homeRouteFor(confirmedUser), { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'invalid_two_factor_code') {
        setError(ru.login.errorTwoFactor);
      } else if (isNetworkError(cause)) {
        setError(ru.login.errorNetwork);
      } else {
        setError(errorMessage(cause, ru.common.unknownError));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-panel flex-col justify-center gap-4 p-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-extrabold">{ru.login.title}</h1>
        <p className="text-sm text-muted">{ru.login.subtitle}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        <Chip active={mode === 'login'} onClick={() => setMode('login')}>
          {ru.login.submit}
        </Chip>
        <Chip active={mode === 'registration'} onClick={() => setMode('registration')}>
          {ru.registration.title}
        </Chip>
      </div>

      {mode === 'registration' ? (
        <Card title={ru.registration.title}>
          <p className="text-xs text-muted">{ru.registration.subtitle}</p>
          <RegistrationForm onGoToLogin={() => setMode('login')} />
        </Card>
      ) : (
        <Card>
          {challengeId ? (
            <form className="flex flex-col gap-3" onSubmit={(event) => void submitCode(event)}>
              <div className="flex flex-col gap-1">
                <strong className="text-sm">{ru.login.twoFactorTitle}</strong>
                <p className="text-xs text-muted">{ru.login.twoFactorHint}</p>
              </div>
              <Field
                label={ru.login.twoFactorCode}
                value={code}
                onChange={setCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
              {error ? (
                <p role="alert" className="text-xs text-danger">
                  {error}
                </p>
              ) : null}
              <Button type="submit" tone="accent" disabled={busy || code.trim().length === 0}>
                {busy ? ru.login.submitting : ru.login.twoFactorSubmit}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setChallengeId(null);
                  setCode('');
                  setError(null);
                }}
              >
                {ru.login.twoFactorBack}
              </Button>
            </form>
          ) : (
            <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
              <Field
                label={ru.login.email}
                value={email}
                onChange={setEmail}
                type="email"
                inputMode="email"
                autoComplete="username"
                placeholder={ru.login.emailPlaceholder}
                required
              />
              <Field
                label={ru.login.password}
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="current-password"
                required
              />
              <p className="text-xs text-muted">{ru.login.passwordHint}</p>
              {error ? (
                <p role="alert" className="text-xs text-danger">
                  {error}
                </p>
              ) : null}
              <Button type="submit" tone="accent" disabled={busy}>
                {busy ? ru.login.submitting : ru.login.submit}
              </Button>
            </form>
          )}
        </Card>
      )}

      {mode === 'login' ? (
        <button
          type="button"
          onClick={() => setMode('registration')}
          className="min-h-touch self-start rounded-button border border-line px-3 text-sm font-semibold text-accent"
        >
          {ru.login.registerLink}
        </button>
      ) : null}

      <p className="text-xs text-muted">
        {mode === 'login' ? ru.login.registerHint : ru.registration.support}
      </p>
    </div>
  );
}
